import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Check, ChevronRight } from 'lucide-react';

import { agentPrompt, apiUrlLine, curlSnippet, GITHUB_WORKFLOW, highlight, SDK_EXAMPLE, sdkInstall, sdkTarball } from '../components/code';
import { KeyCreateForm, KeyReveal } from '../components/KeyCreate';
import { TryIt } from '../components/TryIt';
import { CopyAction, CopyButton, ErrorCallout, Id, Lamp, PageHead, StatePill, Studio } from '../components/ui';
import { ago, moneyFine, plural, repoName } from '../lib/format';
import { apiText } from '../lib/api';
import { useDocumentTitle, useNow, useResource } from '../lib/hooks';
import { useApiOrigin } from '../lib/plane-config';
import { useSession } from '../lib/session';
import type { ApiKey, CreatedKey, Pricing, Sandbox, TaskSummary } from '../lib/types';
import { createdByLabel, failureLabel } from './Tasks';

type Tab = 'prompt' | 'curl' | 'sdk' | 'gha';
const TABS: { id: Tab; label: string; copy: string }[] = [
  { id: 'prompt', label: 'Agent prompt', copy: 'Copy prompt' },
  { id: 'curl', label: 'curl', copy: 'Copy commands' },
  { id: 'sdk', label: 'TypeScript SDK', copy: 'Copy code' },
  { id: 'gha', label: 'GitHub Actions', copy: 'Copy workflow' },
];

/**
 * The versioned SDK tarball the docs install (so a lockfile's integrity hash
 * stays true), read from /llms.txt; the stable name until that answers.
 */
function useSdkTarball(origin: string): string {
  const [url, setUrl] = useState(() => sdkTarball(origin));
  useEffect(() => {
    const ctrl = new AbortController();
    apiText('/llms.txt', ctrl.signal).then(
      (text) => {
        const found = text.match(/https?:\/\/[^\s'"`)<>]+\/sdk\/offstage-cloud-[^\s'"`)<>/]+\.tgz/);
        if (found) setUrl(found[0]);
      },
      () => undefined,
    );
    return () => ctrl.abort();
  }, [origin]);
  return url;
}

function SetupLine({ text }: { text: string }) {
  return (
    <div className="row" style={{ justifyContent: 'space-between', padding: '10px 10px 10px 16px', borderBottom: '1px solid var(--panel-guide)', flexWrap: 'nowrap' }}>
      <pre className="nowrap-code" style={{ minWidth: 0 }}>
        <span className="c">$ </span>
        {highlight(text, 'sh')}
      </pre>
      <CopyButton text={text} label={`Copy ${text.startsWith('npm') ? 'install command' : 'export line'}`} />
    </div>
  );
}

function PromptPanel({ origin, onCopied }: { origin: string; onCopied: () => void }) {
  const [tab, setTab] = useState<Tab>('prompt');
  const tarball = useSdkTarball(origin);
  const texts: Record<Tab, string> = useMemo(
    () => ({ prompt: agentPrompt(origin), curl: curlSnippet(origin), sdk: SDK_EXAMPLE, gha: GITHUB_WORKFLOW }),
    [origin, tarball],
  );
  const current = TABS.find((t) => t.id === tab)!;
  const tabRefs = useRef<Record<Tab, HTMLButtonElement | null>>({ prompt: null, curl: null, sdk: null, gha: null });

  const onKey = (e: React.KeyboardEvent) => {
    const i = TABS.findIndex((t) => t.id === tab);
    let next = i;
    if (e.key === 'ArrowRight') next = (i + 1) % TABS.length;
    else if (e.key === 'ArrowLeft') next = (i - 1 + TABS.length) % TABS.length;
    else return;
    e.preventDefault();
    setTab(TABS[next].id);
    tabRefs.current[TABS[next].id]?.focus();
  };

  return (
    <section className="panel" aria-label="Instructions for your agent">
      <div className="panel__bar">
        <div className="seg" role="tablist" aria-label="Format" onKeyDown={onKey}>
          {TABS.map((t) => (
            <button
              key={t.id}
              ref={(el) => (tabRefs.current[t.id] = el)}
              type="button"
              role="tab"
              id={`tab-${t.id}`}
              aria-selected={tab === t.id}
              aria-controls={`tabpanel-${t.id}`}
              tabIndex={tab === t.id ? 0 : -1}
              onClick={() => setTab(t.id)}
            >
              {t.label}
            </button>
          ))}
        </div>
        <span onClick={onCopied}>
          <CopyAction text={texts[tab]} label={current.copy} primary size="lg" />
        </span>
      </div>
      <div className="panel__full" role="tabpanel" id={`tabpanel-${tab}`} aria-labelledby={`tab-${tab}`} tabIndex={0}>
        {tab === 'sdk' ? (
          <>
            <SetupLine text={sdkInstall(tarball)} />
            <SetupLine text={apiUrlLine(origin)} />
            <pre className="nowrap-code panel__body">{highlight(texts.sdk, 'ts')}</pre>
          </>
        ) : tab === 'gha' ? (
          <>
            <pre className="nowrap-code panel__body">{highlight(texts.gha, 'yaml')}</pre>
            <p className="panel__note">
              Connect your GitHub organization on the <Link to="/ci">CI page</Link> first. Each job then gets a fresh Mac, wiped when it ends.
            </p>
          </>
        ) : (
          <pre className={`panel__body${tab === 'curl' ? ' nowrap-code' : ''}`}>{highlight(texts[tab], tab === 'curl' ? 'sh' : 'text')}</pre>
        )}
      </div>
    </section>
  );
}

type Item =
  | { type: 'task'; id: string; createdAt: number; task: TaskSummary }
  | { type: 'sandbox'; id: string; createdAt: number; sandbox: Sandbox };

/** The team's newest tasks and sandboxes, asked every 3 s. */
function useActivity() {
  const tasks = useResource<{ tasks: TaskSummary[] }>('/v1/tasks?limit=6', { interval: 3000 });
  const sandboxes = useResource<{ sandboxes: Sandbox[] }>('/v1/sandboxes?limit=6', { interval: 3000 });
  const items: Item[] = useMemo(() => {
    const list: Item[] = [
      ...(tasks.data?.tasks ?? []).map((task) => ({ type: 'task' as const, id: task.id, createdAt: task.createdAt, task })),
      ...(sandboxes.data?.sandboxes ?? []).map((sandbox) => ({ type: 'sandbox' as const, id: sandbox.id, createdAt: sandbox.createdAt, sandbox })),
    ];
    return list.sort((a, b) => b.createdAt - a.createdAt).slice(0, 8);
  }, [tasks.data, sandboxes.data]);
  return {
    items,
    loaded: tasks.data !== undefined && sandboxes.data !== undefined,
    error: tasks.error ?? sandboxes.error,
    reload: () => {
      void tasks.reload();
      void sandboxes.reload();
    },
  };
}

type Activity = ReturnType<typeof useActivity>;

function ActivityList({ activity }: { activity: Activity }) {
  const { items, loaded, error } = activity;
  const now = useNow(5000);
  const seen = useRef<Set<string> | null>(null);
  // Everything on screen at first load is "old"; anything after arrives highlighted.
  if (loaded && seen.current === null) seen.current = new Set(items.map((i) => i.id));
  const fresh = (id: string) => seen.current !== null && !seen.current.has(id);
  useEffect(() => {
    const timer = window.setTimeout(() => items.forEach((i) => seen.current?.add(i.id)), 2500);
    return () => window.clearTimeout(timer);
  }, [items]);

  return (
    <div className="activity">
      <div className="activity__head">
        <span className="live">
          <Lamp tone={error ? 'off' : 'live'} />
          {error ? 'Paused' : 'Live · every 3 s'}
        </span>
        <span className="row small">
          <Link to="/ci">CI</Link>
          <Link to="/sandboxes">Sandboxes</Link>
        </span>
      </div>
      <ul className="activity__list" aria-label="Recent tasks and sandboxes" aria-live="polite">
        {items.map((item) => (
          <li key={item.id} className={`activity__item${fresh(item.id) ? ' is-new' : ''}`}>
            <StatePill state={item.type === 'task' ? item.task.state : item.sandbox.state} />
            <div className="activity__main">
              <Id id={item.id} to={item.type === 'task' ? `/ci/${item.id}` : `/sandboxes/${item.id}`} label={item.type === 'sandbox' ? item.sandbox.name : undefined} />
              <span className="activity__meta">
                {item.type === 'task'
                  ? item.task.state === 'failed' && failureLabel(item.task)
                    ? `${item.task.spec.kind} task · ${failureLabel(item.task)}`
                    : `${item.task.spec.kind} task · ${repoName(item.task.spec.repo)}${item.task.createdBy ? ` · ${createdByLabel(item.task)}` : ''}`
                  : `${item.sandbox.session} sandbox · ${plural(item.sandbox.ops, 'op')}${item.sandbox.createdBy ? ` · ${createdByLabel(item.sandbox)}` : ''}`}
              </span>
            </div>
            <div className="activity__right">
              <b>{moneyFine(item.type === 'task' ? item.task.costUsd : item.sandbox.costUsd)}</b>
              {ago(item.createdAt, now)}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

type StepState = 'done' | 'active' | 'next';

/** One step: a number that turns into a check, a line saying what to do, and, while it matters, the control that does it. */
function Step({ n, state, title, detail, action, children }: { n: number; state: StepState; title: string; detail?: ReactNode; action?: ReactNode; children?: ReactNode }) {
  return (
    <li className={`stepper__step is-${state}`} aria-current={state === 'active' ? 'step' : undefined}>
      <span className="stepper__mark" aria-hidden="true">
        {state === 'done' ? <Check /> : n}
      </span>
      <div className="stepper__main">
        <div className="stepper__head">
          <div className="stepper__text">
            <p className="stepper__title">
              {title}
              {state === 'done' ? <span className="visually-hidden"> (done)</span> : null}
            </p>
            {detail ? <p className="stepper__detail">{detail}</p> : null}
          </div>
          {action ? <div className="stepper__action">{action}</div> : null}
        </div>
        {children ? <div className="stepper__body">{children}</div> : null}
      </div>
    </li>
  );
}

function StepperSkeleton() {
  return (
    <ol className="stepper" aria-busy="true" aria-label="Set up your agent">
      {[0, 1, 2].map((i) => (
        <li className="stepper__step is-next" key={i} aria-hidden="true">
          <span className="stepper__mark" />
          <div className="stepper__main">
            <span className="skeleton" style={{ width: `${46 - i * 8}%` }} />
            <span className="skeleton" style={{ width: `${64 - i * 10}%`, marginTop: 8 }} />
          </div>
        </li>
      ))}
    </ol>
  );
}

/** Copying the prompt is the one step the plane cannot see, so this browser remembers it per team. */
function useRemembered(key: string | null): [boolean, () => void] {
  const [value, setValue] = useState(() => {
    try {
      return key !== null && localStorage.getItem(key) === '1';
    } catch {
      return false;
    }
  });
  const remember = () => {
    setValue(true);
    try {
      if (key !== null) localStorage.setItem(key, '1');
    } catch {
      /* private mode: remembered for this visit only */
    }
  };
  return [value, remember];
}

export function GetStarted() {
  useDocumentTitle('Onboarding guide');
  const origin = useApiOrigin();
  const { me } = useSession();
  const pricing = useResource<Pricing>('/v1/pricing');
  const keys = useResource<{ keys: ApiKey[] }>('/v1/keys');
  const activity = useActivity();
  const [created, setCreated] = useState<CreatedKey | null>(null);
  const [copied, rememberCopied] = useRemembered(me ? `offstage.promptCopied.${me.team.id}` : null);

  const canMake = me?.team.verified ?? false;
  const keyCount = keys.data?.keys.length ?? 0;
  const ran = activity.items.length > 0;
  const done = [keyCount > 0 || created !== null, copied || ran, ran];
  const active = done.indexOf(false);
  const state = (i: number): StepState => (done[i] ? 'done' : i === active ? 'active' : 'next');
  const loaded = (keys.data !== undefined || keys.error !== undefined) && (activity.loaded || activity.error !== undefined);

  const copyPrompt = (primary: boolean, label = 'Copy prompt') => (
    <span onClick={rememberCopied}>
      <CopyAction text={agentPrompt(origin)} label={label} primary={primary} size="sm" />
    </span>
  );

  return (
    <>
      <PageHead
        crumbs={<Link to="/sandboxes">Sandboxes</Link>}
        title="Onboarding guide"
        lede={active === -1 ? 'Your agent is set up. Its sandboxes and tasks show up here as they run.' : 'Hand your agent a key and one prompt. It sets itself up and runs on a real Mac.'}
        actions={
          active === -1 ? (
            <Link className="btn" to="/sandboxes">
              Go to sandboxes
            </Link>
          ) : undefined
        }
      />
      <section className="band">
        <div className="home">
          <div className="home__main">
            {!loaded ? (
              <StepperSkeleton />
            ) : (
              <ol className="stepper" aria-label="Set up your agent">
                <Step
                  n={1}
                  state={state(0)}
                  title="Create an API key"
                  detail={
                    created
                      ? undefined
                      : done[0]
                        ? `Your team has ${plural(keyCount, 'key')}.`
                        : canMake
                          ? 'Your agent signs its calls with it.'
                          : 'Open the link we emailed you first: keys need a verified email.'
                  }
                  action={
                    done[0] && !created ? (
                      <Link className="btn btn--sm btn--quiet" to="/keys">
                        Manage keys
                      </Link>
                    ) : undefined
                  }
                >
                  {created ? (
                    <KeyReveal created={created} onDone={() => setCreated(null)} />
                  ) : !done[0] && canMake ? (
                    <KeyCreateForm
                      defaultName={keyCount === 0 ? 'my-agent' : ''}
                      onCreated={(k) => {
                        setCreated(k);
                        void keys.reload();
                      }}
                    />
                  ) : undefined}
                </Step>
                <Step
                  n={2}
                  state={state(1)}
                  title="Paste the prompt into your agent"
                  detail={done[1] ? (ran ? 'Your agent picked it up.' : 'Copied. Paste it into your agent.') : 'Claude Code, Codex, or any agent that can run a shell.'}
                  action={done[1] ? copyPrompt(false, 'Copy again') : copyPrompt(state(1) === 'active')}
                >
                  {done[1] ? undefined : (
                    <details className="disclose">
                      <summary>
                        <ChevronRight aria-hidden="true" />
                        Show the prompt, or use curl, the SDK or GitHub Actions
                      </summary>
                      <PromptPanel origin={origin} onCopied={rememberCopied} />
                    </details>
                  )}
                </Step>
                <Step
                  n={3}
                  state={state(2)}
                  title="Watch it run"
                  detail={ran ? 'Your agent made its first call.' : 'This checks itself off when its first sandbox or task starts.'}
                >
                  {state(2) === 'active' ? (
                    activity.error && !activity.loaded ? (
                      <ErrorCallout error={activity.error} onRetry={activity.reload} />
                    ) : (
                      <div className="waiting">
                        <Studio waiting />
                        <div>
                          <p className="waiting__title">
                            <Lamp tone="live" />
                            Waiting for your agent’s first call…
                          </p>
                          <p>Or press Run to start a Mac yourself.</p>
                        </div>
                      </div>
                    )
                  ) : undefined}
                </Step>
              </ol>
            )}
            {ran ? (
              <section className="home__recent" aria-labelledby="recent-title">
                <h2 className="h2" id="recent-title">
                  Recent activity
                </h2>
                <ActivityList activity={activity} />
              </section>
            ) : null}
          </div>
          <aside className="home__side" aria-label="Try it on a real Mac">
            <TryIt usdPerHour={pricing.data?.usdPerHour} />
          </aside>
        </div>
      </section>
    </>
  );
}
